import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Column, Row } from 'app/design/primitives/box';
import { Text } from 'app/design/primitives/text';
import { Button } from 'app/design/primitives/button';
import { localeAwareFormat } from 'app/lib/date';
import { festivalCalendarDate } from 'app/lib/festival-date';
import { useFestivalToday } from 'app/hooks/use-festival-today';
import { useUpsertMyProfile } from 'app/network/profile';
import type { Profile } from 'app/types/profile';
import { WhiteLabel } from 'app/types/white-labels';
import { isWhiteLabel } from 'app/utils/helpers';
import { Event, track } from 'app/lib/analytics';
import { FestivalDateEditor } from './festival-date-editor';

export function FestivalSavedDate({ value }: { value: string }) {
  const { t } = useTranslation('festival');
  const today = useFestivalToday();
  return (
    <Row alignItems="center" gap="spacing.general.x2" flexWrap="wrap">
      <Text variant="bodyBase">{localeAwareFormat(festivalCalendarDate(value), 'd MMMM yyyy')}</Text>
      {value < today ? (
        <Column
          backgroundColor="surface.error.subtle"
          borderRadius="radius.sm"
          paddingHorizontal="spacing.general.x2"
          paddingVertical="spacing.general.x1"
        >
          <Text color="text.error" variant="bodyXSmallMedium">
            {t('expired')}
          </Text>
        </Column>
      ) : null}
    </Row>
  );
}

export function FestivalProfileCard({ profile }: { profile?: Profile }) {
  const { t } = useTranslation('festival');
  const [open, setOpen] = useState(false);
  const update = useUpsertMyProfile({
    onSuccess: () => {
      setOpen(false);
      track(Event.FestivalDateUpdated, { source: 'profile' });
    },
  });
  if (!isWhiteLabel(WhiteLabel.Refrain) || !profile) return null;
  return (
    <Column
      marginHorizontal="spacing.general.x4"
      borderColor="border.secondary"
      borderWidth={1}
      borderRadius="radius.lg"
      padding="spacing.general.x4"
      gap="spacing.general.x3"
      testID="festival-profile-card"
    >
      <Text variant="bodyBaseMedium">{t('profileTitle')}</Text>
      <Row alignItems="center" justifyContent="space-between" gap="spacing.general.x3" flexWrap="wrap">
        <Column flexShrink={1}>
          {profile.festivalStartDate ? (
            <FestivalSavedDate value={profile.festivalStartDate} />
          ) : (
            <Text color="text.secondary">{t('missingDate')}</Text>
          )}
        </Column>
        <Button
          variant="plain"
          padding="spacing.general.0"
          onPress={() => {
            update.reset();
            setOpen(true);
          }}
        >
          <Text color="text.interactive" variant="bodyBaseMedium">
            {t('edit')}
          </Text>
        </Button>
      </Row>
      {open ? (
        <FestivalDateEditor
          value={profile.festivalStartDate}
          onClose={() => setOpen(false)}
          onSave={(date) => update.mutate({ festivalStartDate: date })}
          pending={update.isPending}
          error={update.error}
        />
      ) : null}
    </Column>
  );
}
